import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Percent, Headphones, Globe2, RefreshCcw, Send, Loader2, CheckCircle2, Users, Briefcase, Scale, Building2, ArrowDown, ArrowRight, AlertCircle } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";
import { setJsonLd, clearJsonLd } from "@/lib/seo";

const TIERS = [
  ["Starter", "Below USD 3,000", "3%"],
  ["Bronze", "USD 3,000 to below USD 10,000", "5%"],
  ["Silver", "USD 10,000 to below USD 25,000", "7%"],
  ["Gold", "USD 25,000 to below USD 50,000", "9%"],
  ["Platinum", "USD 50,000+", "Up to 12%"],
];

const SERVICE_RATES = [
  ["Company Formation", "3%", "5%", "7%", "9%", "12%"],
  ["Bank Account Opening Assistance", "3%", "5%", "7%", "9%", "12%"],
  ["Registered Office / Corporate Services", "3%", "5%", "7%", "9%", "12%"],
  ["Accounting & Tax", "3%", "5%", "7%", "9%", "12%"],
  ["Licensing / Regulatory Services", "3%", "3%", "4%", "5%", "5%"],
];

const WHO = [
  { icon: Briefcase, title: "Accountants & tax professionals", body: "For practices whose clients are expanding abroad, incorporating overseas or need ongoing accounting and corporate support in a new jurisdiction." },
  { icon: Scale, title: "Lawyers & legal advisers", body: "For advisers whose clients ask about cross-border structuring, company formation or licensing and need a delivery partner to execute the work." },
  { icon: Users, title: "Business & company-formation consultants", body: "For consultants who advise on market entry and want a reliable team to handle incorporation, filings and banking introductions." },
  { icon: Building2, title: "Agencies & professional service providers", body: "For agencies and B2B providers whose clients are building internationally and need corporate services alongside your own offering." },
];

const WHY = [
  { icon: Percent, title: "Competitive Commissions", body: "Earn commission based on eligible referral revenue, partner tier and service category, with rates up to 12%." },
  { icon: Headphones, title: "Dedicated Partner Support", body: "Receive support throughout referral and client onboarding." },
  { icon: Globe2, title: "International Business Services", body: "Connect clients with company formation, banking assistance, licensing and corporate services." },
  { icon: RefreshCcw, title: "Recurring Opportunities", body: "Eligible ongoing services may create further commission opportunities, subject to the Partner Agreement." },
];

const STEPS = [
  { n: "01", title: "Refer", body: "Introduce qualified clients to UNICORP." },
  { n: "02", title: "We Handle the Process", body: "UNICORP manages consultation, documentation, onboarding and service delivery." },
  { n: "03", title: "Earn", body: "Earn commission on eligible, paid referrals according to your partner tier and service category." },
  { n: "04", title: "Grow", body: "Higher monthly eligible referral revenue can qualify you for higher tiers, with service-specific rates applying." },
];

const TERMS = [
  "Rates depend on partner tier and service category.",
  "The maximum standard rate is 12% on eligible referrals.",
  "Government fees, statutory fees and other non-commissionable amounts are excluded where applicable.",
  "Special or high-value projects may have individually agreed commission terms.",
  "Commission is subject to successful client payment and the applicable Partner Agreement.",
];

const FAQS = [
  { q: "Who can apply to become a UNICORP partner?", a: "Accountants and tax professionals, lawyers and legal advisers, business and company-formation consultants, agencies and other professional service providers are welcome to apply. Applications are reviewed individually, and participation does not override your own professional or regulatory obligations." },
  { q: "What services can I refer clients for?", a: "You can refer clients for international company formation, bank account opening assistance, registered office and corporate services, accounting & tax support, and licensing / regulatory services. UNICORP supports applications and service delivery; bank or regulatory approval remains with the relevant institution or authority." },
  { q: "How is my partner tier determined?", a: "Partner tiers are assessed monthly based on eligible referral revenue. The applicable commission rate also depends on the service category." },
  { q: "How much commission can I earn?", a: "Commission ranges from 3% to a maximum standard rate of 12%, depending on your monthly-assessed partner tier and the service category. Licensing and regulatory services carry commission rates of 3%–5%." },
  { q: "Are government and statutory fees included?", a: "No. Commission is calculated on the eligible amount of the client invoice for the applicable service, excluding government fees, statutory fees and other non-commissionable amounts where applicable." },
  { q: "When are commissions paid?", a: "Commissions are calculated at the end of each calendar month, and approved commissions are paid in the following month, subject to verification of eligible client payments." },
  { q: "Can ongoing services generate further commission?", a: "Eligible ongoing services may create further commission opportunities, subject to the Partner Agreement. Not all renewals automatically earn commission." },
  { q: "How are my referrals tracked and attributed?", a: "When you apply and are approved, your referrals are attributed to your partner account based on the details you submit and the method agreed at onboarding. Exact attribution windows and tracking are confirmed in your Partner Agreement." },
  { q: "In what currency and how often are commissions paid?", a: "Approved commissions are calculated monthly and paid in the following month. Payout currency, any minimum payout threshold and the payment method are confirmed in your Partner Agreement before your first payout." },
  { q: "Who handles client onboarding and service delivery?", a: "UNICORP manages consultation, documentation, onboarding and service delivery for referred clients, so you can stay focused on your own practice." },
];

const PARTNER_TYPES = ["Accountant / Tax professional", "Lawyer / Legal adviser", "Business / Company-formation consultant", "Agency / Professional service provider", "Other"];
const SERVICE_OPTIONS = ["Company Formation", "Bank Account Opening Assistance", "Registered Office / Corporate Services", "Accounting & Tax", "Licensing / Regulatory Services"];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function PartnershipPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", country: "", partner_type: "", phone: "", website: "", services: [], message: "", hp: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    setJsonLd("partner-schema", {
      "@context": "https://schema.org", "@type": "WebPage",
      name: "UNICORP Partner Program",
      description: "Refer clients for company formation, banking assistance and corporate services. Explore UNICORP partner tiers and earn up to 12% on eligible referrals.",
    });
    setJsonLd("partner-breadcrumb", {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "/" },
        { "@type": "ListItem", position: 2, name: "Partner Program" },
      ],
    });
    return () => { clearJsonLd("partner-schema"); clearJsonLd("partner-breadcrumb"); };
  }, []);

  const toggleService = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your full name.";
    if (!form.email.includes("@") || !form.email.split("@")[1]?.includes(".")) e.email = "Please enter a valid business email.";
    if (!form.company.trim()) e.company = "Please enter your company or practice name.";
    if (!form.country.trim()) e.country = "Please enter your country or region.";
    if (!form.partner_type) e.partner_type = "Please select your partner type.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (busy || sent) return;
    if (!validate()) { toast.error("Please complete the required fields."); return; }
    setBusy(true);
    try {
      await api.post("/contact", {
        name: form.name, email: form.email, company: form.company, phone: form.phone,
        country: form.country, partner_type: form.partner_type, website: form.website,
        services: form.services, message: form.message, type: "partner", hp: form.hp,
      });
      trackEvent("generate_lead", { form: "partner_program", service: form.partner_type || "unspecified" });
      setSent(true);
      setTimeout(() => scrollTo("apply"), 60);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again or email info@uniincorp.com.");
    } finally { setBusy(false); }
  };

  const Err = ({ k }) => errors[k] ? <p id={`pp-${k}-error`} className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid={`partner-error-${k}`}><AlertCircle className="h-3 w-3" />{errors[k]}</p> : null;

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* A. Hero */}
      <section className="relative grain pt-36 pb-20 border-b border-border bg-secondary/40">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs uppercase tracking-[0.2em] text-orange-600">Partner Program</p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-primary">Grow with UNICORP.</h1>
            <p className="mt-5 text-muted-foreground text-lg leading-relaxed max-w-2xl">
              Accountants, lawyers, consultants, agencies and other professional service providers can create an
              additional revenue opportunity by referring clients for international company formation, bank account
              opening assistance, licensing and corporate services. UNICORP handles consultation, onboarding and
              service delivery — you stay focused on your clients.
            </p>
            <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-medium text-orange-800" data-testid="partner-hero-rate">
              <Percent className="h-4 w-4" /> Earn up to 12% on eligible referrals
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => scrollTo("apply")} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-12 px-8" data-testid="partner-cta-apply">
                Apply to Become a Partner <ArrowDown className="h-4 w-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => scrollTo("rates")} className="rounded-sm border-slate-300 bg-card hover:bg-secondary h-12 px-8" data-testid="partner-cta-rates">
                View Commission Rates
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* B. Who the programme is for */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
        <div className="max-w-2xl">
          <h2 className="font-heading text-3xl tracking-tight text-primary">Who the programme is for</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            The programme suits professionals whose clients already ask about doing business internationally —
            for example a client exploring overseas company formation, seeking bank account opening assistance, or
            needing accounting and corporate support in another jurisdiction. Applying is not automatic qualification,
            and participation never overrides your own professional obligations.
          </p>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 gap-5">
          {WHO.map((w, i) => (
            <motion.div key={w.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
              className="rounded-xl border border-border bg-card p-6 card-soft" data-testid={`partner-who-${i}`}>
              <span className="grid place-items-center h-10 w-10 rounded-sm bg-orange-50 text-orange-600 border border-orange-100"><w.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-heading text-lg text-primary">{w.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.body}</p>
            </motion.div>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Explore what you can refer: <Link to="/company-formation" className="text-orange-700 hover:underline">company formation</Link>,{" "}
          <Link to="/banks" className="text-orange-700 hover:underline">banking &amp; fintech accounts</Link> and{" "}
          <Link to="/more-services" className="text-orange-700 hover:underline">licensing &amp; corporate services</Link>.
        </p>
      </section>

      {/* C. Why partner with UNICORP */}
      <section className="border-y border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <h2 className="font-heading text-3xl tracking-tight text-primary">Why partner with UNICORP</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-xl border border-border bg-card p-6 card-soft" data-testid={`partner-benefit-${i}`}>
                <span className="grid place-items-center h-10 w-10 rounded-sm bg-secondary text-primary border border-border"><b.icon className="h-5 w-5" /></span>
                <h3 className="mt-4 font-heading text-lg text-primary">{b.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* D + E + F. Commission rates */}
      <section id="rates" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20 scroll-mt-28">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] text-orange-600">Commission</p>
          <h2 className="mt-3 font-heading text-3xl tracking-tight text-primary">Partner commission tiers</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            Partner tiers are assessed monthly based on eligible referral revenue. The applicable commission rate
            also depends on the service category.
          </p>
        </div>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-card card-soft" data-testid="partner-tier-table">
          <table className="w-full min-w-[560px] text-sm">
            <caption className="sr-only">Partner commission tiers by monthly eligible referral revenue</caption>
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-left">
                <th scope="col" className="px-5 py-4 font-heading text-primary">Partner Tier</th>
                <th scope="col" className="px-5 py-4 font-heading text-primary">Monthly Eligible Referral Revenue</th>
                <th scope="col" className="px-5 py-4 font-heading text-primary">Commission</th>
              </tr>
            </thead>
            <tbody>
              {TIERS.map(([tier, range, rate]) => (
                <tr key={tier} className="border-b border-border last:border-0" data-testid={`partner-tier-${tier.toLowerCase()}`}>
                  <th scope="row" className="px-5 py-4 text-left font-medium text-foreground">{tier}</th>
                  <td className="px-5 py-4 text-muted-foreground">{range}</td>
                  <td className="px-5 py-4 font-mono text-orange-700">{rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mt-16 font-heading text-3xl tracking-tight text-primary">Commission by service</h2>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-card card-soft" data-testid="partner-service-table">
          <table className="w-full min-w-[720px] text-sm">
            <caption className="sr-only">Commission rates by service category and partner tier</caption>
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-left">
                <th scope="col" className="px-5 py-4 font-heading text-primary">Service Category</th>
                {["Starter", "Bronze", "Silver", "Gold", "Platinum"].map((t) => (
                  <th key={t} scope="col" className="px-4 py-4 font-heading text-primary text-center">{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SERVICE_RATES.map(([svc, ...rates]) => (
                <tr key={svc} className="border-b border-border last:border-0" data-testid={`partner-svc-${svc.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <th scope="row" className="px-5 py-4 text-left font-medium text-foreground">{svc}</th>
                  {rates.map((r, i) => <td key={i} className="px-4 py-4 text-center font-mono text-muted-foreground">{r}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-3xl" data-testid="partner-exclusion-note">
          Commission is calculated on the eligible amount of the client invoice for the applicable service, excluding
          government fees, statutory fees and other non-commissionable amounts where applicable. Licensing and
          regulatory services carry commission rates of 3%–5%.
        </p>

        <div className="mt-8 rounded-xl border border-border bg-secondary/40 p-6 max-w-3xl" data-testid="partner-example">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Worked example</p>
          <p className="mt-2 text-foreground leading-relaxed">
            For a Bronze partner, a company formation invoice with an eligible commissionable amount of USD 2,000
            would generate <span className="font-medium">USD 100 in commission at 5%</span>.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            This is an illustration only: it assumes Bronze status applies and that the referral meets the eligibility
            and payment requirements. A single invoice of this size would not by itself qualify a partner for the Bronze tier.
          </p>
        </div>
      </section>

      {/* G. How it works */}
      <section className="border-y border-border bg-primary text-primary-foreground grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <h2 className="font-heading text-3xl tracking-tight">How it works</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
                className="rounded-xl border border-white/10 bg-white/5 p-6" data-testid={`partner-step-${i}`}>
                <p className="font-mono text-sm text-orange-400">{s.n}</p>
                <h3 className="mt-3 font-heading text-lg">{s.title}</h3>
                <p className="mt-2 text-sm text-primary-foreground/70 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* H + I. Payment & essential terms */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <h2 className="font-heading text-3xl tracking-tight text-primary">Monthly commission payment</h2>
          <ul className="mt-6 space-y-3.5 text-muted-foreground leading-relaxed">
            <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" /> Commissions are calculated at the end of each calendar month.</li>
            <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" /> Approved commissions are paid in the following month, subject to verification of eligible client payments.</li>
            <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" /> Refunds, cancellations and chargebacks may affect commission.</li>
            <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" /> Eligibility and payment are governed by the applicable Partner Agreement and UNICORP&apos;s policies.</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-border bg-card p-7 card-soft">
          <h2 className="font-heading text-2xl tracking-tight text-primary">Essential commission terms</h2>
          <ul className="mt-5 space-y-3">
            {TERMS.map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-sm text-slate-700">
                <span className="grid place-items-center h-4 w-4 mt-0.5 rounded-full bg-orange-100 shrink-0"><CheckCircle2 className="h-3 w-3 text-orange-700" /></span>{t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* J. FAQs */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-20">
        <h2 className="font-heading text-3xl tracking-tight text-primary mb-8">Frequently asked questions</h2>
        <div className="space-y-4" data-testid="partner-faqs">
          {FAQS.map((f, i) => (
            <details key={i} className="group rounded-xl border border-border bg-card px-5 py-4 card-soft" data-testid={`partner-faq-${i}`}>
              <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-foreground font-medium">
                {f.q}
                <ArrowRight className="h-4 w-4 text-orange-600 shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* K. Final CTA + application form */}
      <section id="apply" className="border-t border-border bg-secondary/40 scroll-mt-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-primary">Build your business with UNICORP.</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Ready to connect your clients with UNICORP&apos;s international business services? Tell us about your
              practice and the clients you typically support.
            </p>
          </div>

          <div className="mt-10 rounded-2xl border border-border bg-card p-6 sm:p-9 card-soft" data-testid="partner-form-card">
            {sent ? (
              <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center" data-testid="partner-success" role="status">
                <CheckCircle2 className="h-8 w-8 mx-auto text-green-600" />
                <p className="mt-3 font-heading text-lg text-primary">Application received</p>
                <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                  Thank you for your interest in partnering with UNICORP. Your application has been received, and our
                  team will review your details.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="space-y-5" data-testid="partner-form">
                {/* honeypot — hidden from humans */}
                <input type="text" value={form.hp} onChange={set("hp")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid="partner-hp" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="pp-name" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Full name *</label>
                    <Input id="pp-name" value={form.name} onChange={set("name")} aria-invalid={!!errors.name} aria-describedby={errors.name ? "pp-name-error" : undefined} placeholder="Jane Partner" className="mt-2 bg-background border-border" data-testid="partner-name" />
                    <Err k="name" />
                  </div>
                  <div>
                    <label htmlFor="pp-email" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Business email *</label>
                    <Input id="pp-email" type="email" value={form.email} onChange={set("email")} aria-invalid={!!errors.email} aria-describedby={errors.email ? "pp-email-error" : undefined} placeholder="you@firm.com" className="mt-2 bg-background border-border" data-testid="partner-email" />
                    <Err k="email" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="pp-company" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Company / practice name *</label>
                    <Input id="pp-company" value={form.company} onChange={set("company")} aria-invalid={!!errors.company} aria-describedby={errors.company ? "pp-company-error" : undefined} placeholder="Your firm" className="mt-2 bg-background border-border" data-testid="partner-company" />
                    <Err k="company" />
                  </div>
                  <div>
                    <label htmlFor="pp-country" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Country / region *</label>
                    <Input id="pp-country" value={form.country} onChange={set("country")} aria-invalid={!!errors.country} aria-describedby={errors.country ? "pp-country-error" : undefined} placeholder="e.g. United Kingdom" className="mt-2 bg-background border-border" data-testid="partner-country" />
                    <Err k="country" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Partner type *</label>
                    <Select value={form.partner_type} onValueChange={set("partner_type")}>
                      <SelectTrigger aria-invalid={!!errors.partner_type} aria-describedby={errors.partner_type ? "pp-partner_type-error" : undefined} className="mt-2 bg-background border-border" data-testid="partner-type"><SelectValue placeholder="Select your profile" /></SelectTrigger>
                      <SelectContent>{PARTNER_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                    <Err k="partner_type" />
                  </div>
                  <div>
                    <label htmlFor="pp-phone" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Phone / WhatsApp (with country code)</label>
                    <Input id="pp-phone" value={form.phone} onChange={set("phone")} placeholder="+44 …" className="mt-2 bg-background border-border" data-testid="partner-phone" />
                  </div>
                </div>
                <div>
                  <label htmlFor="pp-website" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Website</label>
                  <Input id="pp-website" value={form.website} onChange={set("website")} placeholder="https://yourfirm.com" className="mt-2 bg-background border-border" data-testid="partner-website" />
                </div>
                <fieldset>
                  <legend className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Services of interest (optional)</legend>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {SERVICE_OPTIONS.map((s) => (
                      <button type="button" key={s} onClick={() => toggleService(s)} aria-pressed={form.services.includes(s)}
                        className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${form.services.includes(s) ? "border-orange-400 bg-orange-50 text-orange-800" : "border-border bg-background text-muted-foreground hover:text-foreground"}`}
                        data-testid={`partner-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </fieldset>
                <div>
                  <label htmlFor="pp-message" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Brief introduction / expected referral needs</label>
                  <Textarea id="pp-message" value={form.message} onChange={set("message")} placeholder="Tell us about your practice and the clients you expect to refer…" className="mt-2 min-h-[110px] bg-background border-border" data-testid="partner-message" />
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  By submitting this form you agree that UNICORP may process your details to review your application,
                  as described in our <Link to="/legal/privacy" className="text-orange-700 hover:underline">Privacy Policy</Link>.
                </p>
                <Button type="submit" disabled={busy} size="lg" className="w-full rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-12" data-testid="partner-submit">
                  {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting…</> : <>Submit Partner Application <Send className="h-4 w-4 ml-2" /></>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
